/**
 * Feature flags for gating in-progress functionality.
 *
 * Each flag has a `defaultEnabled` state and optional `allowedRegions`.
 *
 * Precedence (highest wins):
 *   1. URL: `?features=flag1,flag2`
 *   2. Per-user override in localStorage (set via the Settings → Experimental UI)
 *   3. `defaultEnabled` in the config
 *
 * URL wins over localStorage so deep-links and bug-bash links continue to work
 * exactly as documented, regardless of what the user has toggled.
 *
 * Adding a new flag:
 *   1. Add entry to featureFlagConfig below
 *   2. Use isFeatureEnabled('flagName') in non-components / module scope
 *   3. Use useFeatureFlag('flagName') in React components (auto-provides region)
 *   4. Activate via URL: ?features=flagName, or toggle in Settings → Experimental
 */

interface FeatureFlagDef {
  /** Human-readable label rendered in Settings → Experimental. */
  label: string;
  /** One-line description rendered under the label. */
  description: string;
  /** Whether this flag is active without URL/localStorage activation */
  defaultEnabled: boolean;
  /** If set, flag is only active in these regions (case-insensitive). No region context = flag off. */
  allowedRegions?: readonly string[];
  /**
   * Hide this flag from the Settings → Experimental UI. The flag stays
   * in the registry, so `?features=` deep-links and localStorage overrides
   * keep working — it just isn't surfaced as a user-toggleable option.
   *
   * Use for flags that are still in development, or for flags that have
   * shipped but are kept around briefly before deletion.
   */
  hidden?: boolean;
}

// Internal config with literal key inference
const _config = {
  knowledgeManagement: {
    label: 'Knowledge management',
    description: 'Enables the Knowledge Base page and the agent “Knowledge” tab.',
    defaultEnabled: true,
    hidden: true,
  },
  workflowTemplates: {
    label: 'Workflow templates',
    description:
      'Browse and start from prebuilt templates when creating a workflow, with a Templates tab in the designer.',
    defaultEnabled: true,
    hidden: true,
  },
  scopedEdit: {
    label: 'Scoped edits with the assistant',
    description:
      'Select one or more nodes on the canvas and ask the assistant to change only the selection, instead of editing the whole workflow.',
    defaultEnabled: false,
  },
  workflowAssistant: {
    label: 'Workflow assistant hero',
    description:
      'Shows the AI prompt section on the workflow list page; when off, that section is hidden.',
    defaultEnabled: false,
  },
  assistantGitHubAndHistory: {
    label: 'Assistant: GitHub sign-in and history',
    description:
      'Sign into the workflow assistant with GitHub and revisit previous chat sessions from the History tab.',
    defaultEnabled: false,
    hidden: true,
  },
  githubCopilotConnection: {
    label: 'GitHub Copilot agent connections',
    description:
      'Adds GitHub Copilot (OAuth sign-in) as a model provider for coding agents. Requires a runtime that understands the `GitHubCopilot` agent-connection type — keep this off where the runtime predates it, otherwise the connection cannot be deserialized.',
    defaultEnabled: false,
    hidden: true,
  },
  generateTemplate: {
    label: 'Generate template from run',
    description:
      'Adds a "Generate Template" button to the run monitoring toolbar that exports a reusable .recipe.json from a succeeded run.',
    defaultEnabled: false,
    hidden: true,
  },
  legacyAdoConnectorApi: {
    label: 'Legacy Azure DevOps connector API',
    description:
      'Routes repository OAuth through the legacy `ottoazuredevops` connector instead of the new endpoint. For diagnosing compatibility issues with older deployments.',
    defaultEnabled: false,
    hidden: true,
  },
  hostedAgents: {
    label: 'Hosted agents',
    description:
      'Enables hosted agent management and the hosted agent option in workflow agent authoring.',
    defaultEnabled: false,
    hidden: false,
  },
} satisfies Record<string, FeatureFlagDef>;

/**
 * Whether a flag should appear in the Settings → Experimental UI.
 * Flags without `hidden: true` are shown.
 */
export function isExperimentalFlagVisible(name: FeatureFlagName): boolean {
  return !featureFlagConfig[name].hidden;
}

/** Valid feature flag names derived from the config. */
export type FeatureFlagName = keyof typeof _config;

/** Feature flag definitions — typed for proper access to all FeatureFlagDef properties. */
export const featureFlagConfig: Record<FeatureFlagName, FeatureFlagDef> = _config;

// --- URL parsing (cached once per page load) ---

let _urlFeatures: Set<string> | null = null;

function getUrlFeatures(): Set<string> {
  if (_urlFeatures === null) {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('features') ?? '';
    _urlFeatures = new Set(
      raw.split(',').map(s => s.trim()).filter(Boolean),
    );

    if (import.meta.env.DEV) {
      for (const name of _urlFeatures) {
        if (!Object.hasOwn(featureFlagConfig, name)) {
          console.warn(
            `[FeatureFlags] Unknown flag "${name}" in URL. Valid flags: ${Object.keys(featureFlagConfig).join(', ')}`,
          );
        }
      }
    }
  }
  return _urlFeatures;
}

// --- Per-user overrides (localStorage, cached after first read) ---

export const OVERRIDES_KEY = 'otto:feature-flag-overrides';

type OverrideMap = Partial<Record<FeatureFlagName, boolean>>;

// Module-scope cache for the parsed override map. Invalidated on
// in-tab writes (`setFeatureFlagOverride`) and on cross-tab `storage`
// events. `useFeatureFlag` calls `isFeatureEnabled` from inside React
// useSyncExternalStore's getSnapshot — which fires on every render —
// so we cannot afford a localStorage.getItem + JSON.parse per render.
let _overridesCache: OverrideMap | null = null;

function parseOverridesFromStorage(): OverrideMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(OVERRIDES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: OverrideMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (Object.hasOwn(featureFlagConfig, k) && typeof v === 'boolean') {
        out[k as FeatureFlagName] = v;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function readOverrides(): OverrideMap {
  if (_overridesCache === null) {
    _overridesCache = parseOverridesFromStorage();
  }
  return _overridesCache;
}

function invalidateOverridesCache(): void {
  _overridesCache = null;
}

// Listen for cross-tab updates once at module load. Filter by key so an
// unrelated localStorage write doesn't blow the cache for nothing.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === null || e.key === OVERRIDES_KEY) {
      invalidateOverridesCache();
    }
  });
}

function writeOverrides(overrides: OverrideMap): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(OVERRIDES_KEY, JSON.stringify(overrides));
  } catch {
    // Quota exceeded / private mode — silently skip; user toggle simply won't persist.
  }
}

/**
 * Returns the current set of per-user flag overrides.
 *
 * Reads from the module-scope cache, which is invalidated after in-tab writes
 * and relevant cross-tab storage events. Returns a fresh copy so callers can't
 * accidentally mutate the cached map.
 */
export function getFeatureFlagOverrides(): OverrideMap {
  return { ...readOverrides() };
}

/**
 * Toggle a feature flag override. Pass `undefined` to clear (revert to default).
 * Emits a `storage`-like event so subscribers can react to changes.
 */
export function setFeatureFlagOverride(flag: FeatureFlagName, value: boolean | undefined): void {
  // Mutate via readOverrides() copy + writeOverrides, then invalidate so
  // the next read pulls the canonical post-write state from storage.
  const current = { ...readOverrides() };
  if (value === undefined) {
    delete current[flag];
  } else {
    current[flag] = value;
  }
  writeOverrides(current);
  invalidateOverridesCache();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('otto:feature-flags-changed'));
  }
}

/**
 * Subscribe to in-tab and cross-tab feature flag changes. Calls the
 * callback whenever a relevant write happens. Returns an unsubscribe
 * function for cleanup.
 *
 * "Relevant" = the in-tab CustomEvent OR a storage event whose key is
 * the overrides key (or `null` for `localStorage.clear()`).
 */
export function subscribeToFeatureFlagChanges(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handleStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === OVERRIDES_KEY) callback();
  };
  window.addEventListener('otto:feature-flags-changed', callback);
  window.addEventListener('storage', handleStorage);
  return () => {
    window.removeEventListener('otto:feature-flags-changed', callback);
    window.removeEventListener('storage', handleStorage);
  };
}

/**
 * Check if a feature flag is active.
 *
 * Pure function — no hooks, no React context. Safe to call anywhere:
 * module scope, non-components, outside Router.
 *
 * A flag is active when (in order of precedence):
 *   1. listed in `?features=` URL param, OR
 *   2. has a per-user override in localStorage, OR
 *   3. `defaultEnabled` is true.
 *
 * If the flag has allowedRegions, the current region must match.
 */
export function isFeatureEnabled(flag: FeatureFlagName, region?: string): boolean {
  const config = featureFlagConfig[flag];

  const urlOn = getUrlFeatures().has(flag);
  const override = readOverrides()[flag];
  const active = urlOn || (override !== undefined ? override : config.defaultEnabled);
  if (!active) return false;

  if (config.allowedRegions && config.allowedRegions.length > 0) {
    if (!region) return false;
    return config.allowedRegions.some(r => r.toLowerCase() === region.toLowerCase());
  }

  return true;
}

/**
 * Returns names of all currently active flags (for telemetry/debugging).
 * Evaluates without a region, so region-scoped flags (those with allowedRegions)
 * are intentionally excluded — they require router context to resolve.
 */
export function getActiveFeatureFlags(): FeatureFlagName[] {
  return (Object.keys(featureFlagConfig) as FeatureFlagName[]).filter(
    flag => isFeatureEnabled(flag),
  );
}

/** Whether a flag is forced on by the URL — used by the Settings UI to show
 *  a "URL override" indicator and disable the toggle. */
export function isFeatureFlagForcedByUrl(flag: FeatureFlagName): boolean {
  return getUrlFeatures().has(flag);
}

/** Reset the URL feature cache (for testing only). */
export function _resetUrlFeaturesCache(): void {
  _urlFeatures = null;
}

/** Reset the overrides cache (for testing only). */
export function _resetOverridesCache(): void {
  _overridesCache = null;
}
